import React from 'react';
import PropTypes from 'prop-types';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Box, Flex, Text } from 'atoms';
import { EyeIconSVG } from 'utils/Svg';
import { ACL } from 'components/common';

const RequestCard = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 24px;
	margin-bottom: 16px;
	transition: box-shadow 0.3s;

	&:hover {
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}
`;

const CardBody = styled(Flex)`
	align-items: flex-start;
	justify-content: space-between;
	gap: 24px;
`;

const InfoColumns = styled(Box)`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 20px 40px;
	flex: 1;
`;

const InfoField = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	font-weight: 400;
	line-height: 1.4;
`;

const FieldValue = styled(Text)`
	font-size: 15px;
	color: #000;
	font-weight: 600;
	line-height: 1.5;
	word-break: break-word;
`;

const ActionSection = styled(Flex)`
	flex-direction: row;
	align-items: center;
	justify-content: center;
	gap: 12px;
	flex-shrink: 0;
	align-self: center;
`;

const ViewButton = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 4px;
	cursor: pointer;
	margin-right: 15px;

	/* The icon's own wrapper carries a 24px width and a right margin; neutralise both so the
	   icon sits dead centre in the box and the hover highlight is a symmetric square. */
	span {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px !important;
		height: 20px !important;
		margin-right: 0 !important;
	}

	svg {
		width: 20px !important;
		height: 20px !important;
	}

	&:hover {
		background: #bae7ff;
	}
`;

const ApproveButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 18px;
	min-width: 130px;
	background: #fff;
	color: #52c41a;
	border: 1.5px solid #52c41a;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.25s ease;

	&:hover {
		background: #f6ffed;
		border-color: #73d13d;
		color: #73d13d;
	}
`;

const RejectButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	padding: 10px 20px;
	min-width: 130px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s;

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
	}
`;

/**
 * One pending Driver / Vehicle review. `fields` are the label/value pairs from
 * verificationConfig; View opens the details page where every document can be opened.
 */
export default function VerificationApprovalCardV2({
	fields,
	aclModule,
	onView,
	onApprove,
	onReject,
}) {
	return (
		<RequestCard>
			<CardBody>
				<InfoColumns>
					{fields.map((field) => (
						<InfoField key={field.key}>
							<FieldLabel>{field.label} :</FieldLabel>
							<FieldValue>{field.value}</FieldValue>
						</InfoField>
					))}
				</InfoColumns>
				<ActionSection>
					<ViewButton onClick={onView} title="View documents">
						<EyeIconSVG />
					</ViewButton>
					<ACL action="update" module={aclModule}>
						<ApproveButton onClick={onApprove}>
							<CheckOutlined />
							Approve
						</ApproveButton>
					</ACL>
					<ACL action="update" module={aclModule}>
						<RejectButton onClick={onReject}>
							<CloseOutlined />
							Reject
						</RejectButton>
					</ACL>
				</ActionSection>
			</CardBody>
		</RequestCard>
	);
}

VerificationApprovalCardV2.propTypes = {
	fields: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			value: PropTypes.node,
		})
	).isRequired,
	aclModule: PropTypes.string.isRequired,
	onView: PropTypes.func.isRequired,
	onApprove: PropTypes.func.isRequired,
	onReject: PropTypes.func.isRequired,
};
